/**
 * SDMS Template Builder — Data Validation Rules Manager: the rules for the CURRENT
 * SELECTION or a whole worksheet, with New / Edit / Delete. Works on a copy of the rule
 * list; OK hands the edited list back to the editor (written to the .xlsx on Save as
 * standard Excel data validation), Cancel throws it away. Same shape as the Conditional
 * Formatting Rules Manager; validation rules have no evaluation order, so no Up / Down.
 */
import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useConfirm } from '../../../components/confirm';
import { absoluteSqref, sqrefIntersects, type CellBox } from './cfModel';
import { describeDv, EDITABLE_DV_TYPES, EMPTY_DV, newDvRule, type DvDefinition, type DvRule } from './dvModel';
import { DataValidationDialog } from './DataValidationDialog';
import { colName } from './templateWorkbook';

const SELECTION = 'sel';

const ERROR_LABEL: Record<string, string> = { stop: 'Stop', warning: 'Warning', information: 'Information' };

export function DataValidationManager({ open, newRuleOnly, sheetNames, rules, selection, onApply, onClose, readOnly }: {
  open: boolean;
  /** Ribbon "New Rule…": show only the rule dialog; OK adds the rule, Cancel closes. */
  newRuleOnly?: boolean;
  /** worksheet names in workbook order (index = position + 1) */
  sheetNames: string[];
  rules: DvRule[];
  /** the sheet selection when the manager opened (null = none) */
  selection: ({ sheetIndex: number } & CellBox) | null;
  onApply: (rules: DvRule[]) => void;
  onClose: () => void;
  readOnly?: boolean;
}) {
  const { alert, confirm } = useConfirm();
  const [work, setWork] = useState<DvRule[]>([]);
  const [scope, setScope] = useState<string>(SELECTION);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ mode: 'new' | 'edit'; sheetIndex: number; initial: DvDefinition } | null>(null);

  useEffect(() => {
    if (!open) return;
    setWork(rules);
    setScope(selection ? SELECTION : String(1));
    setActiveKey(null);
    setEditor(newRuleOnly ? { mode: 'new', sheetIndex: selection?.sheetIndex ?? 1, initial: { ...EMPTY_DV, range: selection ? boxSqref(selection) : '' } } : null);
  }, [open, rules, selection, newRuleOnly]);

  const scopeSheet = scope === SELECTION ? selection?.sheetIndex ?? 1 : Number(scope);
  const visible = useMemo(() => work.filter(r => r.sheetIndex === scopeSheet
    && (scope !== SELECTION || !selection || sqrefIntersects(r.range, selection))), [work, scope, scopeSheet, selection]);
  const active = work.find(r => r.key === activeKey) ?? null;
  const sheetName = (i: number) => sheetNames[i - 1] ?? `Sheet${i}`;
  const selectionSqref = selection ? boxSqref(selection) : '';

  const openNew = () => setEditor({ mode: 'new', sheetIndex: scopeSheet, initial: { ...EMPTY_DV, range: scope === SELECTION ? selectionSqref : '' } });
  const openEdit = (rule = active) => {
    if (!rule) return;
    if (!EDITABLE_DV_TYPES.has(rule.type)) {
      void alert({ title: 'Edit Rule', message: `"${describeDv(rule)}" rules can be deleted here but not edited.`, tone: 'info' });
      return;
    }
    setEditor({ mode: 'edit', sheetIndex: rule.sheetIndex, initial: rule });
  };
  const onEditorDone = (def: DvDefinition) => {
    if (!editor) return;
    if (editor.mode === 'new') {
      const rule = newDvRule(editor.sheetIndex, def);
      const next = [...work, rule];
      if (newRuleOnly) { setEditor(null); onApply(next); return; }
      setWork(next);
      setActiveKey(rule.key);
    } else {
      setWork(w => w.map(r => (r.key === activeKey ? { ...r, ...def } : r)));
    }
    // A rule outside the selection would vanish from "Current Selection" — show its sheet instead.
    if (scope === SELECTION && selection && !sqrefIntersects(def.range, selection)) setScope(String(editor.sheetIndex));
    setEditor(null);
  };
  const remove = async () => {
    if (!active) return;
    if (!(await confirm({ title: 'Delete Rule', message: `Delete the rule "${describeDv(active)}" for ${absoluteSqref(active.range)}?`, tone: 'danger' }))) return;
    setWork(w => w.filter(r => r.key !== active.key));
    setActiveKey(null);
  };

  const columns: AppColumn<DvRule>[] = [
    { field: 'rule', header: 'Rule', width: 300, sortable: false, value: r => describeDv(r), text: r => describeDv(r) },
    { field: 'range', header: 'Applies to', width: 190, sortable: false, value: r => absoluteSqref(r.range), text: r => absoluteSqref(r.range) },
    { field: 'error', header: 'Error alert', width: 110, sortable: false, value: r => (r.showErrorMessage ? ERROR_LABEL[r.errorStyle] ?? r.errorStyle : 'Off') },
    { field: 'input', header: 'Input message', width: 170, sortable: false, value: r => (r.showInputMessage ? r.inputTitle || r.inputMessage || 'On' : 'Off') },
  ];

  return (
    <>
      <AppDialog open={open && !newRuleOnly} title="Data Validation Rules Manager" icon="ti-checklist" width={880} onClose={onClose}
        submitOnEnter={false}
        footer={<>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid" disabled={readOnly} onClick={() => onApply(work)}>OK</button>
        </>}>
        <div className="stb-cfm-scope">
          <label htmlFor="dvm-scope">Show validation rules for:</label>
          <select id="dvm-scope" className="sd-input" value={scope} onChange={e => { setScope(e.target.value); setActiveKey(null); }}>
            {selection && <option value={SELECTION}>Current Selection ({sheetName(selection.sheetIndex)}!{selectionSqref})</option>}
            {sheetNames.map((n, i) => <option key={n} value={String(i + 1)}>This Worksheet: {n}</option>)}
          </select>
        </div>
        <div className="stb-actions">
          <button type="button" className="ca-btn" disabled={readOnly} onClick={openNew}><i className="ti ti-plus" /> New Rule…</button>
          <button type="button" className="ca-btn" disabled={readOnly || !active} onClick={() => openEdit()}><i className="ti ti-edit" /> Edit Rule…</button>
          <button type="button" className="ca-btn" disabled={readOnly || !active} onClick={() => void remove()}><i className="ti ti-trash" /> Delete Rule</button>
        </div>
        <div className="stb-cfm-grid">
          <AppDataGrid<DvRule> rows={visible} columns={columns} getRowId={r => r.key} defaultPageSize={50} hideFooter
            hideHeaderTools selectedRowId={activeKey ?? undefined}
            onRowClick={r => setActiveKey(r.key)} onRowDoubleClick={r => { setActiveKey(r.key); if (!readOnly) openEdit(r); }}
            emptyText={scope === SELECTION ? 'No validation rules apply to the selected cells.' : 'No validation rules on this worksheet.'} />
        </div>
      </AppDialog>
      <DataValidationDialog open={editor != null} mode={editor?.mode ?? 'new'} sheetName={sheetName(editor?.sheetIndex ?? 1)}
        initial={editor?.initial ?? EMPTY_DV} onDone={onEditorDone}
        onClose={() => { setEditor(null); if (newRuleOnly) onClose(); }} />
    </>
  );
}

const boxSqref = (b: CellBox) => {
  const a = `${colName(b.startCol)}${b.startRow + 1}`;
  return b.endRow !== b.startRow || b.endCol !== b.startCol ? `${a}:${colName(b.endCol)}${b.endRow + 1}` : a;
};
